import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart2 } from 'lucide-react';

const SIP_FAQS = [
  { q: 'How much SIP is needed for 1 crore in 10 years?', a: 'At 12% returns, approximately ₹43,000/month. At 15%, around ₹38,000/month. The power of compounding means starting earlier makes a huge difference.' },
  { q: 'Is SIP safe?', a: 'SIP in equity funds carries market risk, but over 7+ years, Indian equity SIPs have historically delivered 12–15% annual returns, beating inflation comfortably.' },
  { q: 'Can I stop SIP anytime?', a: 'Yes. Unlike FDs, most SIPs have no lock-in (except ELSS — 3 years). You can pause, reduce, or stop without penalty.' },
  { q: 'What is the minimum SIP amount?', a: 'Most funds allow ₹500/month. Some like Axis, Mirae, and ICICI allow ₹100/month SIPs through direct plans.' },
  { q: 'SIP vs lump sum — which is better?', a: 'SIP is better for salaried investors — rupee-cost averaging removes the need to time the market. Lump sum can outperform in a strong bull run.' },
  { q: 'How is SIP return measured?', a: 'Via XIRR (Extended Internal Rate of Return), which accounts for different investment dates. It is equivalent to CAGR for lump sums.' },
];

export default function CalcSip() {
  document.title = 'SIP Calculator India — Budget Mantra';
  const [monthly, setMonthly] = useState('');
  const [rate, setRate]       = useState('');
  const [years, setYears]     = useState('');
  const [result, setResult]   = useState(null);

  const calculate = () => {
    const P = parseFloat(monthly);
    const r = parseFloat(rate) / 12 / 100;
    const n = parseFloat(years) * 12;
    if (!P || !r || !n) return;
    const fv = P * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
    setResult({ fv, invested: P * n });
  };

  const fmt = (n) => Math.round(n).toLocaleString('en-IN');

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
          <BarChart2 size={20} className="text-orange-500" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-stone-900">SIP Calculator</h1>
          <p className="text-sm text-stone-500">See how your SIP grows over time</p>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4 max-w-md">
        <div>
          <label className="block text-xs font-bold text-stone-500 mb-1.5">Monthly SIP Amount (₹)</label>
          <input type="number" value={monthly} onChange={e => setMonthly(e.target.value)} placeholder="5000"
            className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
        </div>
        <div>
          <label className="block text-xs font-bold text-stone-500 mb-1.5">Expected Annual Return (%)</label>
          <input type="number" value={rate} onChange={e => setRate(e.target.value)} placeholder="12"
            className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
        </div>
        <div>
          <label className="block text-xs font-bold text-stone-500 mb-1.5">Duration (years)</label>
          <input type="number" value={years} onChange={e => setYears(e.target.value)} placeholder="10"
            className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
        </div>
        <button onClick={calculate}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-2.5 rounded-xl transition-colors">
          Calculate Returns
        </button>
        {result && (
          <div className="bg-orange-50 rounded-xl p-4 text-center">
            <p className="text-xs text-stone-500 mb-1">Maturity Value</p>
            <p className="text-3xl font-extrabold text-orange-600">₹{fmt(result.fv)}</p>
            <p className="text-xs text-stone-500 mt-2">Invested: ₹{fmt(result.invested)} · Gains: ₹{fmt(result.fv - result.invested)}</p>
          </div>
        )}
      </div>
      <p className="mt-6 text-sm text-stone-500">
        Track your investments →{' '}
        <Link to="/login" className="text-orange-500 font-semibold hover:underline">Sign up free</Link>
      </p>
      <div className="mt-10 max-w-2xl space-y-8 text-stone-700 dark:text-stone-300">
        <section>
          <h2 className="text-lg font-extrabold text-stone-900 dark:text-stone-100 mb-2">What is a SIP?</h2>
          <p className="text-sm leading-7">A SIP (Systematic Investment Plan) lets you invest a fixed amount in a mutual fund every month. Instead of timing the market, SIP uses rupee-cost averaging — you buy more units when prices are low, fewer when high, smoothing out market volatility over time.</p>
        </section>
        <section>
          <h2 className="text-lg font-extrabold text-stone-900 dark:text-stone-100 mb-2">How is SIP return calculated?</h2>
          <p className="text-sm leading-7">Maturity = P × [(1+r)^n – 1] / r × (1+r) where P = monthly amount, r = monthly return rate, n = months. Example: ₹5,000/month at 12% for 10 years → ₹11.6 lakhs maturity. Invested: ₹6L. Gains: ₹5.6L.</p>
        </section>
        <section>
          <h2 className="text-lg font-extrabold text-stone-900 dark:text-stone-100 mb-3">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {SIP_FAQS.map(faq => (
              <div key={faq.q} className="border-b border-stone-100 dark:border-stone-800 pb-4">
                <p className="text-sm font-bold text-stone-900 dark:text-stone-100 mb-1">{faq.q}</p>
                <p className="text-sm leading-6 text-stone-600 dark:text-stone-400">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="text-lg font-extrabold text-stone-900 dark:text-stone-100 mb-2">Related Calculators</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/calc/emi" className="text-sm font-semibold text-orange-500 hover:underline">EMI Calculator →</Link>
            <Link to="/calc/salary" className="text-sm font-semibold text-orange-500 hover:underline">In-Hand Salary →</Link>
            <Link to="/calc/tax" className="text-sm font-semibold text-orange-500 hover:underline">Income Tax →</Link>
          </div>
        </section>
        <section className="bg-orange-50 dark:bg-orange-950/30 rounded-2xl p-5">
          <p className="text-xs font-bold text-orange-500 uppercase tracking-wide mb-1">Chanakya says</p>
          <p className="text-sm leading-6 text-stone-700 dark:text-stone-300 mb-4">
            {result
              ? `₹${fmt(result.fv)} at maturity — ₹${fmt(result.fv - result.invested)} are pure gains. Want me to track this SIP against your goals?`
              : `Enter your SIP details and I'll show you how it stacks up against your financial goals.`}
          </p>
          <Link to="/signup" className="inline-block bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold px-5 py-2 rounded-xl transition-colors">
            Ask Chanakya free →
          </Link>
        </section>
      </div>
    </div>
  );
}
